import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Path, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { getAssetCategoriesQueryOptions } from '@/queries/asset-categories';

const formSchema = z.object({
  categoryId: z.string({ message: 'Required' }),
});

function BulkUpdateAssetCategoryDialog({
  open,
  onOpenChange,
  callback,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback: (categoryId: string, resetForm: () => void) => void;
  loading: boolean;
}) {
  const { data: assetCategories } = useQuery(getAssetCategoriesQueryOptions());

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      categoryId: undefined,
    },
  });

  function handleSubmission(data: z.infer<typeof formSchema>) {
    callback(data.categoryId, () => form.reset());
    onOpenChange(false);
  }

  function handleOpenChange(open: boolean) {
    onOpenChange(open);
    if (!open) {
      form.reset();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col gap-[10px]">
        <DialogTitle>Edit Asset Category</DialogTitle>

        <DialogDescription>Bulk editing the Asset Category will change all of the selected assets to the Asset Category you specify below.</DialogDescription>

        <Form {...form}>
          <form className="mt-2 flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmission)}>
            <FormFieldItem<z.infer<typeof formSchema>, Path<z.infer<typeof formSchema>>>
              control={form.control}
              name="categoryId"
              label="Asset Category"
              labelSize="small">
              {({ value, onChange, error }) => (
                <Combobox
                  aria-label="Asset Category"
                  value={value}
                  onChange={onChange}
                  options={assetCategories?.map(({ id, name }) => ({ label: name, value: id })) || []}
                  className={cn('w-full', error && 'border-default-red')}
                />
              )}
            </FormFieldItem>

            <div className="mt-4">
              <Button variant="success" type="submit" disabled={loading}>
                Save
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { BulkUpdateAssetCategoryDialog };
